import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useRouterState } from '@tanstack/react-router'
import {
  numberLocale,
  pilotAnalyticsDimensions,
  useLocale,
  useT,
} from '@/shared/i18n'
import { minifyJson } from '@/shared/json'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'json-minifier'

export function JsonMinifierPage() {
  const t = useT()
  const { locale } = useLocale()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const localeCode = numberLocale(locale)
  const analyticsDimensions = useMemo(
    () => pilotAnalyticsDimensions(pathname, locale),
    [locale, pathname],
  )
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    const saved = input.length > 0 ? Math.max(input.length - output.length, 0) : 0
    return { chars, lines, saved }
  }, [input, output])

  const handleMinify = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setStatusMessage(null)
      return
    }

    const start = performance.now()
    setStatusMessage(null)
    toolInvoke(UTILITY, { action: 'minify', ...analyticsDimensions })

    const result = minifyJson(input)
    if (result.ok) {
      setOutput(result.result)
      setError(null)
      setStatusMessage(t.jsonMinifier.minified)
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        action: 'minify',
        ...analyticsDimensions,
      })
    } else {
      setOutput('')
      setError(t.jsonMinifier.errors.invalidJson.replace('{error}', result.error))
      setStatusMessage(null)
      toolError(UTILITY, 'invalid-json', { action: 'minify', ...analyticsDimensions })
    }
  }, [analyticsDimensions, input, t])

  useEffect(() => {
    const timeout = window.setTimeout(handleMinify, 200)
    return () => window.clearTimeout(timeout)
  }, [handleMinify])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setStatusMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="jsonMinifier" width="wide">
      <CodeWorkspace
        error={error}
        notice={statusMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-3">
            <Button data-code-run onClick={handleMinify} disabled={!input.trim()}>
              {t.jsonMinifier.minify}
            </Button>
          </div>
        )}
        input={{
          title: t.jsonMinifier.input,
          value: input,
          clear: { label: t.jsonMinifier.clear, onClick: handleClear },
          onChange: (value) => {
            setInput(value)
            setOutput('')
            setError(null)
            setStatusMessage(null)
          },
          placeholder: t.jsonMinifier.inputPlaceholder,
          ariaLabel: t.jsonMinifier.input,
          stats: [
            {
              label: t.jsonMinifier.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.jsonMinifier.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.jsonMinifier.output,
          value: output,
          readOnly: true,
          placeholder: t.jsonMinifier.outputPlaceholder,
          ariaLabel: t.jsonMinifier.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.jsonMinifier.copied : t.jsonMinifier.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.jsonMinifier.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.jsonMinifier.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
              {
                label: t.jsonMinifier.saved,
                value: outputStats.saved.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.jsonMinifier.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.jsonMinifier.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.jsonMinifier.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.jsonMinifier.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
